<script setup lang="ts">
import type { Agency } from "@asap/shared/schemas/agency";
import type { BusinessLifecycleStage } from "@asap/shared/types/supabase.types.gen";
import type { TableEmptyStateType } from "~/utils/types";
import { translateBusinessLifecycleStage } from "@asap/shared/utils/translate";
import { useToast } from "~/composables/useToast";
import { useAgencies } from "~/components/agency/composables/useAgency";
import { useTableColumn } from "~/composables/useTableColumn";

const props = defineProps<{
  companyId: string;
}>();

const { t } = useI18n();
const { addToastError } = useToast();
const { agencyState, selectMultipleForTable, convertSortingToOrders } = useAgencies();

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: t("companies.agencies.columns.status"),
      value: {
        accessorKey: "status",
        header: t("companies.agencies.columns.status"),
      },
      blocked: true,
      selected: true,
    },
    {
      label: t("companies.agencies.columns.city"),
      value: {
        accessorKey: "city",
        header: t("companies.agencies.columns.city"),
      },
      blocked: true,
      selected: true,
    },
    {
      label: t("companies.agencies.columns.street"),
      value: {
        accessorKey: "street",
        header: t("companies.agencies.columns.street"),
        minSize: 300,
      },
      blocked: true,
      selected: true,
    },
    {
      label: t("companies.agencies.columns.contracts"),
      value: {
        accessorKey: "contracts",
        header: t("companies.agencies.columns.contracts"),
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: t("companies.agencies.columns.siret"),
      value: {
        accessorKey: "siret",
        header: t("companies.agencies.columns.siret"),
      },
      blocked: true,
      selected: true,
    },
  ],
  "agencies-table-columns"
);

const agencies = ref<Agency[]>([]);
const emptyStateType = ref<TableEmptyStateType>("no-data");
const isInitialized = ref(false);

const filterableStatuses: BusinessLifecycleStage[] = ["lead", "prospect", "client"];

const statusOptions = computed(() =>
  filterableStatuses.map((status) => ({
    label: translateBusinessLifecycleStage.fr[status],
    value: status,
  }))
);

onMounted(async () => {
  agencyState.selectedFilters.pageIndex = 0;
  await query();
  isInitialized.value = true;
});

watch(
  () => agencyState.selectedFilters.pageIndex,
  () => query()
);

watch(
  () => [
    agencyState.sorting,
    agencyState.selectedFilters.search,
    agencyState.selectedFilters.pageSize,
    agencyState.selectedFilters.status,
    agencyState.selectedFilters.companyId,
    agencyState.selectedFilters.orders,
  ],
  async () => {
    if (!isInitialized.value) return;
    agencyState.selectedFilters.pageIndex = 0;
    await query();
  }
);

const resetFilters = () => {
  agencyState.selectedFilters.search = undefined;
  agencyState.selectedFilters.status = undefined;
  agencyState.sorting = [{ id: "created_at", desc: true }];
};

const query = async () => {
  try {
    const orders = convertSortingToOrders(agencyState.sorting);
    const { data, count } = await selectMultipleForTable({
      ...agencyState.selectedFilters,
      companyId: props.companyId,
      orders,
    });
    if (data) agencies.value = data;
    if (count !== null) agencyState.totalItems = count;
    emptyStateType.value = agencyState.selectedFilters.search ? "no-data-with-filters" : "no-data";
  } catch (error) {
    emptyStateType.value = "error";
    addToastError({ title: t("companies.agencies.toast.loadError") }, error);
  }
};

const ui = {
  container: "flex flex-col h-full flex-1",
  inlineCell: "flex flex-row gap-1 items-center",
};
</script>

<template>
  <div :class="ui.container">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <AwTextField
        v-model="agencyState.selectedFilters.search"
        leading-icon="i-ph-magnifying-glass"
        :placeholder="$t('companies.agencies.searchPlaceholder')"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <TableStatusSelect v-model:status="agencyState.selectedFilters.status" :status-options="statusOptions" />
      <TableFiltersResetButton @click="resetFilters" />
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="agencyState.sorting"
      :loading="agencyState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :data="agencies"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div :class="ui.inlineCell">
            <AwBadge v-if="row.original.is_ceased" color="critical" :label="$t('companies.agencies.closed')" />
            <AwBadge
              v-else-if="row.original.is_ceased === false"
              color="success"
              :label="$t('companies.agencies.active')"
            />
            <ContactStatusBadge :status="row.original.status" />
          </div>
        </TableCellContainer>
      </template>
      <template #city-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div :class="ui.inlineCell">
            <TextEllipsis>
              {{ row.original.address?.city ?? "-" }}
            </TextEllipsis>
            <div v-if="row.original.address?.postal_code">({{ row.original.address?.postal_code }})</div>
            <AwBadge v-if="row.original.is_head_office" color="blue" :label="$t('companies.agencies.headOffice')" />
          </div>
        </TableCellContainer>
      </template>
      <template #street-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div :class="ui.inlineCell">
            <TextEllipsis>
              {{ row.original.address?.street1 ?? "-" }}
              {{ row.original.address?.street2 }}
            </TextEllipsis>
            <Copy v-if="row.original.address?.formatted" :text="row.original.address?.formatted ?? ''" />
          </div>
        </TableCellContainer>
      </template>
      <template #contracts-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['amount']">
          <TextEllipsis>
            <div :class="ui.inlineCell">
              <AwIcon name="i-ph-file-text" class="size-4 text-secondary-700" />
              {{ row.original.contracts.length }}
            </div>
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #siret-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <TextEllipsis>
            <div v-if="row.original.siret" class="font-medium flex items-center gap-2">
              {{ row.original.siret }}
              <Copy v-if="row.original.siret" :text="row.original.siret ?? ''" />
            </div>
            <div v-else>-</div>
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #footer>
        <TablePagination
          :total-items="agencyState.totalItems"
          :page-size="agencyState.selectedFilters.pageSize"
          :page-index="agencyState.selectedFilters.pageIndex"
          @update:page-size="agencyState.selectedFilters.pageSize = $event"
          @update:page-index="agencyState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
  </div>
</template>
